/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { v4 as uuidv4 } from 'uuid';

// Misc imports
import { ACTIVITY_TYPES, CHAPTERSOURCE_SCHEMAS } from '../../constants/main';
import { GRAPH_LAYOUTS, NODE_TYPES } from '../../constants/flowGraph';

/**
 * Returns the ID of the knowledge base to display for a given automated answer.
 * Knowledge base ID is the second part of the AA ID, separated by a dash (eg. kb-knowledgeBaseId-somethingelse).
 * @param {Object} automatedAnswer The related AA
 * @returns {String|Null} The ID of the knowledge base to display, or null if not found.
*/
export const getKnowledgeBaseId = (automatedAnswer) => {
  return automatedAnswer?.id?.split('-')[1] || null;
};

/**
 * Returns the ID of the article to display for a given automated answer.
 * Article ID is the third part of the AA ID, separated by a dash (eg. kb-knowledgeBaseId-articleId).
 * @param {Object} automatedAnswer The related AA
 * @returns {String|Null} The ID of the article to display, or null if not found.
*/
export const getArticleId = (automatedAnswer) => {
  return automatedAnswer?.id?.split('-')[2] || null;
};

/**
 * @param {String} automatedAnswerId The chaptersource ID, typically a UUID
 * @param {String} automatedAnswerDisplayName The chaptersource display name, which could contain special characters (in the primary bot language)
 * @returns A unique, but readable string for use as the intro node ID
 */
export const generateIntroNodeId = (automatedAnswerId, automatedAnswerDisplayName) => {
  const sanitizedDisplayName = automatedAnswerDisplayName.replaceAll('[^a-zA-Z0-9]', ''); // Remove special characters and spaces
  const regExp = /(.*?)-/; // Match everything before the first dash
  var matches = regExp.exec(automatedAnswerId);

  if (matches) {
    return `${sanitizedDisplayName}-${matches[1]}`;
  } else {
    return sanitizedDisplayName;
  }
};

/**
 * Used to populate the form with initial values
 * @param {Object} automatedAnswer The initial values, in case we're editing an existing item
 * @param {String} primaryBotLanguage The primary bot language, needed to set initial values in case we're creating a new item
 * @returns An object to set in the 'initialValues' prop of the form
 */
export const chaptersourceToAutomatedAnswer = (automatedAnswer, primaryBotLanguage) => {
  if (automatedAnswer) {
    return {
      ...automatedAnswer,
      responses: automatedAnswer.content.nodes.filter(node => node.type !== NODE_TYPES.INTRO).map(node => {
        return {
          type: node.type,
          ...((node.type === NODE_TYPES.MESSAGE || node.type === NODE_TYPES.KNOWLEDGE_BASE_ARTICLE) && { options: [node.label] }),
          ...(node.type === NODE_TYPES.JUMP && { options: [{}], jumpTo: node.jumpTo }),
          ...(node.type === NODE_TYPES.USER_FEEDBACK && { options: [{}], jumpTo: node.jumpTo, jumpProbability: node.jumpProbability || 1 }),
          ...(node.type === NODE_TYPES.VARIABLE && { options: [{}], variable: node.variable }),
          // Knowledge base article
          ...(node.type === NODE_TYPES.KNOWLEDGE_BASE_ARTICLE && { useGeneratedResponse: node.useGeneratedResponse ?? automatedAnswer.settings?.useGeneratedResponse ?? false }),
          // Thumbs up/down feedback
          ...(node?.inlineFeedbackData && { enableThumbsFeedback: true }),
          ...(node?.inlineFeedbackData && { inlineFeedbackData: node.inlineFeedbackData })
        };
      })
    };
  } else {
    return {
      responses: [{
        type: NODE_TYPES.MESSAGE,
        options: [
          ''
        ]
      }],
      nlpTrainingData: {
        examples: {
          [primaryBotLanguage]: ['']
        }
      }
    };
  }
};

/**
 * Transforms an automated answer to a FLOWGRAPH chaptersource
 *
 * @param {Array} automatedAnswer The automated answer that was generated by AutomatedAnswerForm
 * @param {String} primaryBotLanguage The primary bot language
 * @param {String} introNodeId The ID of the intro node, in case we want to override the current uuid logic
 * @param {String} type The activity type of the chaptersource
 * @returns A chaptersource containing nodes and edges (a flow graph) based on the provided automated answer
 */
export const automatedAnswerToChaptersource = (automatedAnswer, primaryBotLanguage, introNodeId, type = ACTIVITY_TYPES.AUTOMATED_ANSWERS) => {
  if (!automatedAnswer || !automatedAnswer.name?.[primaryBotLanguage]) {
    return null;
  }

  let lastNodePositionY = 100;
  const chaptersourceId = automatedAnswer.id || uuidv4();

  let edges = [];
  const nodesFromResponses = [{
    id: introNodeId || generateIntroNodeId(chaptersourceId, automatedAnswer.name[primaryBotLanguage]),
    type: NODE_TYPES.INTRO,
    label: { [primaryBotLanguage]: '' },
    position: {
      x: 200,
      y: lastNodePositionY
    }
  },
  ...(automatedAnswer.responses || []).map(response => {
    lastNodePositionY += 150;

    return {
      id: uuidv4(),
      type: response.type,
      label: (response.type === NODE_TYPES.MESSAGE || response.type === NODE_TYPES.KNOWLEDGE_BASE_ARTICLE) && response.options[0] ? response.options[0] : { [primaryBotLanguage]: '' },
      ...(response.type === NODE_TYPES.KNOWLEDGE_BASE_ARTICLE && { useGeneratedResponse: response.useGeneratedResponse }),
      ...(response.inlineFeedbackData && { inlineFeedbackData: response.inlineFeedbackData }),
      ...(response.type === NODE_TYPES.JUMP && { jumpTo: response.jumpTo }),
      ...(response.type === NODE_TYPES.USER_FEEDBACK && { jumpTo: response.jumpTo, jumpProbability: response.jumpProbability || 1 }),
      ...(response.type === NODE_TYPES.VARIABLE && { variable: response.variable }),
      position: {
        x: 200,
        y: lastNodePositionY
      }
    };
  })];

  // To generate the edges we just need to connect the nodes consecutively
  for (let index = 0; index < nodesFromResponses.length; index++) {
    const currentNode = nodesFromResponses[index];
    const previousNode = nodesFromResponses[index - 1];

    if (currentNode && previousNode) {
      edges.push({
        // Connecting intro node with first question
        id: uuidv4(),
        source: previousNode.id,
        target: currentNode.id,
        label: ''
      });
    }
  }

  const chaptersource = {
    name: automatedAnswer.name,
    id: chaptersourceId,
    group: automatedAnswer.group || { [primaryBotLanguage]: 'Default' },
    schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
    type: type,
    content: {
      schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
      layout: GRAPH_LAYOUTS.VERTICAL,
      nodes: nodesFromResponses,
      edges: edges
    },
    settings: {
      ...automatedAnswer.settings || {}
    },
    nlpTrainingData: {
      ...automatedAnswer.nlpTrainingData || {}
    }
  };

  if (type=== ACTIVITY_TYPES.AUTOMATED_ANSWERS) {
    chaptersource.tags = automatedAnswer.tags || [];
  }

  return chaptersource;
};
